import {OUTLINE, SUBTITLE1, TEXT, TOP} from '@/utils/constants';
import {Box, Typography, Tooltip} from '@mui/material';
import {ReactComponent as SendEmailIcon} from '@icons/sidebar/send-email.svg';
import {NotificationSectionStyle} from './NotificationSection.styles';
import Checkbox from '@/shared/components/Checkbox';
import Button from '@/shared/components/Button';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getAttendee, getAttendeeSelector} from '@/store/organization-attendees';
import {ATTENDEE_STATUS, MENU_OPTION, PAYMENT_STATUS, TABLE_STATUS} from '@/utils/enums';
import {OrganizationAttendeesService} from '@/utils/services';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {getCurrentEventSelector} from '@/store/organization-event';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {useEffect, useState} from 'react';
import {CommunicationService} from '@/utils/services/organization-communication.services';
import {ReactComponent as QuestionIcon} from '@/assets/icons/modal-question.svg';


const NotificationSection = () => {
  const dispatch = useAppDispatch();
  const attendee = useAppSelector(getAttendeeSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);

  const isCheckedIn = attendee?.status === ATTENDEE_STATUS.CHECKED_IN;
  const hasContributions = attendee?.paidStatus === PAYMENT_STATUS.PENDING;

  const secondButtonTitle = !isCheckedIn && hasContributions ? 'Check In + Send Payment Link' : 'Send Payment Link';
  const secondButtonDisabled = !hasContributions || attendee?.isUnknown;

  const [emailOption, setEmailOption] = useState<string>('');
  const [notifyOptions, setNotifyOptions] = useState<string[]>([]);

  useEffect(() => {
    const options: string[] = [];
    if (attendee?.isEmailNotificationsEnabled) options.push('Email');
    if (attendee?.isSmsNotificationsEnabled) options.push('Text');
    setNotifyOptions(options);
  }, [attendee?.isEmailNotificationsEnabled, attendee?.isSmsNotificationsEnabled]);

  const handleNotifyOptionChange = async (value: string, checked: boolean) => {
    const newOptions = checked ? [...notifyOptions, value] : notifyOptions.filter((opt) => opt !== value);
    setNotifyOptions(newOptions);

    if (!attendee) return;

    try {
      const reqBody = {
        isEmailNotificationsEnabled: newOptions.includes('Email'),
        isSmsNotificationsEnabled: newOptions.includes('Text'),
      };

      await OrganizationAttendeesService.updateAttendeeNotificationPreferences(attendee.id, reqBody);
      await dispatch(getAttendee(attendee?.id)).unwrap();
      toast.success('Notification preferences updated successfully');
    } catch (error) {
      setNotifyOptions(notifyOptions);
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateAttendeeStatus = () => {
    const newStatus =
      attendee?.status === ATTENDEE_STATUS.CHECKED_IN ? ATTENDEE_STATUS.PENDING : ATTENDEE_STATUS.CHECKED_IN;

    const updateAttendeeStatus = async () => {
      if (!attendee) return;

      try {
        await OrganizationAttendeesService.updateAttendeesStatus(attendee?.id, newStatus);
        await dispatch(getAttendee(attendee?.id)).unwrap();
        toast.success('Attendee status is Checked In');
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    updateAttendeeStatus();
  };

  const handleUpdateAttendeeStatusAndPaymentLink = () => {
    const newStatus = attendee?.status === ATTENDEE_STATUS.CHECKED_IN ? TABLE_STATUS.PENDING : TABLE_STATUS.CHECKED_IN;

    const updateAttendeeStatus = async () => {
      if (!attendee || !currentEvent) return;

      try {
        await OrganizationAttendeesService.updateAttendeesStatus(attendee?.id, newStatus);
        await dispatch(getAttendee(attendee?.id)).unwrap();
        await OrganizationStripeService.sendPaymentLink(attendee.id, currentEvent?.id);

        toast.success('Attendee status is Checked In and payment link is sent');
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    updateAttendeeStatus();
  };

  const handleSendPaymentLink = async () => {
    if (!currentEvent || !attendee) return;

    if (attendee.paidStatus === null) {
      toast.info('Attendee hasn`t any contributions yet');
      return;
    }

    try {
      await OrganizationStripeService.sendPaymentLink(attendee.id, currentEvent?.id);
      toast.success('Payment link sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSendTicket = async () => {
    if (!attendee) return;

    try {
      if (emailOption === 'Email') await CommunicationService.emailAttendeeTicket(attendee?.id);
      else {
        //TODO finish after adding send ticket by text
      }
      toast.success('Ticket sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const secondButtonHandler = isCheckedIn ? handleSendPaymentLink : handleUpdateAttendeeStatusAndPaymentLink;

  return (
    <Box sx={NotificationSectionStyle.wrapper}>
      <Box>
        <Box sx={NotificationSectionStyle.titleWithTooltip}>
          <Typography sx={NotificationSectionStyle.titleWrapper} variant={SUBTITLE1}>
            Notification Preferences
          </Typography>
          <Tooltip
            title="Changing these preferences here will also update the attendee's Notification Settings on their account."
            placement={TOP}
          >
            <Box sx={NotificationSectionStyle.tooltipIcon}>
              <QuestionIcon />
            </Box>
          </Tooltip>
        </Box>
        <Box sx={NotificationSectionStyle.itemsWrapper}>
          <Checkbox
            label="Email"
            disabled={attendee?.isUnknown}
            value={notifyOptions.includes('Email')}
            onChange={(event) => handleNotifyOptionChange('Email', event.target.checked)}
          />
          <Checkbox
            label="Text"
            disabled={attendee?.isUnknown}
            value={notifyOptions.includes('Text')}
            onChange={(event) => handleNotifyOptionChange('Text', event.target.checked)}
          />
        </Box>
        <Button
          title={isCheckedIn ? MENU_OPTION.CANCEL_CHECK_IN : MENU_OPTION.CHECK_IN}
          variant={OUTLINE}
          disabled={attendee?.isUnknown}
          onClick={handleUpdateAttendeeStatus}
        />
      </Box>
      <Box>
        <Button
          title="Resend Ticket"
          variant={TEXT}
          disabled={emailOption === ''}
          endIcon={<SendEmailIcon />}
          sx={{
            marginBottom: '16px',
            ...(emailOption === '' ? NotificationSectionStyle.disableBtn : {}),
          }}
          onClick={handleSendTicket}
        />
        <Box sx={NotificationSectionStyle.itemsWrapper}>
          <Checkbox
            label="Sent ticket email"
            disabled={attendee?.isUnknown}
            value={emailOption === 'Email'}
            onChange={(event) => {
              if (event.target.checked) setEmailOption('Email');
              else setEmailOption('');
            }}
          />
          <Checkbox
            label="Send ticket text"
            disabled={attendee?.isUnknown}
            value={emailOption === 'Text'}
            onChange={(event) => {
              if (event.target.checked) setEmailOption('Text');
              else setEmailOption('');
            }}
          />
        </Box>
        <Button
          title={secondButtonTitle}
          variant={OUTLINE}
          disabled={secondButtonDisabled}
          onClick={secondButtonHandler}
        />
      </Box>
    </Box>
  );
};

export default NotificationSection;
